import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

import { BaseComponent, CommunityService, ContentService, environment, EventBusService, HelperService, UserService, Video } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { ClipboardService } from "ngx-clipboard";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";
import { ChooseLessonComponent, EditVideoComponent } from "../modals";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-video-grid-item",
  templateUrl: "video-grid-item.component.html",
  standalone: false,
})
export class VideoGridItemComponent extends BaseComponent {
  @Input() video: Video;
  @Input() admin: boolean;
  @Input() selectable: boolean;
  @Input() showBorder: boolean;
  @Input() showRemoveFromPost: boolean = false;
  @Input() showRemove: boolean = false;
  @Input() showAdd: boolean = false;
  @Input() showKeepWatchingActions: boolean = false;
  @Input() showLibraryActions: boolean = true;
  @Input() showChevron: boolean = true;
  @Input() showCopyLink: boolean = true;
  @Input() showAddToLesson: boolean = false;
  @Input() showDate: boolean = true;
  @Input() numbered: any;
  @Input() lessonID: any;
  @Input() hideActionButtons: boolean = true;
  /** When set, overrides tabindex on the focusable card (e.g. -1 to skip when rail is collapsed). When null, defaults to 0. */
  @Input() tabIndex: number | null = null;
  /** When true (and in rail), this card's options (Copy link, Add to library, etc.) are in tab order. Set when user presses Enter on the card. */
  @Input() isCardEntered: boolean = false;
  /** 1-based index in the rail list (e.g. 3 for "Item 3 of 10"). When set with totalCount, used in aria-label. */
  @Input() itemIndex: number | null = null;
  /** Total number of items in the rail (e.g. 10). When set with itemIndex, used in aria-label. */
  @Input() totalCount: number | null = null;
  @Output() videoClickedEvent: EventEmitter<Video> = new EventEmitter();
  @Output() cardEntered: EventEmitter<void> = new EventEmitter();
  @Output() exited: EventEmitter<void> = new EventEmitter();
  @Output() added: EventEmitter<void> = new EventEmitter();
  @Output() removed: EventEmitter<void> = new EventEmitter();
  @Output() tagSelected: EventEmitter<Video> = new EventEmitter();
  @Output() removeFromPostClicked: EventEmitter<any> = new EventEmitter();
  @ViewChild("itemContainer") itemContainer: ElementRef;
  @ViewChild("focusableCard", { read: ElementRef }) focusableCard: ElementRef;
  public status: string;
  public displayStatus: string;
  public copied: boolean = false;
  public loadingLibraryAction: boolean = false;
  public videoFormattedDuration: string;
  public videoFormattedLevel: string;

  public youtubePreviewUrl: SafeResourceUrl | null = null;
  private _hoverPreviewTimer: ReturnType<typeof setTimeout> | null = null;

  private _performingListAction: boolean;
  set performingListAction(value) {
    this._performingListAction = value;
    setTimeout(() => {
      this._performingListAction = false;
    }, 500);
  }

  get performingListAction() {
    return this._performingListAction;
  }

  constructor(
    userService: UserService,
    private _clipboardService: ClipboardService,
    public appService: AppService,
    public communityService: CommunityService,
    public contentService: ContentService,
    public helperService: HelperService,
    public eventBusService: EventBusService,
    public router: Router,
    private _sanitizer: DomSanitizer,
    private translate: TranslateService,
    private _elementRef: ElementRef
  ) {
    super(userService);
  }

  onHoverStart() {
    // Touch screens report a tap as a hover: the preview then loaded under the finger as the
    // reader tried to scroll the list. Only devices that really hover get it.
    if (typeof window !== "undefined" && !window.matchMedia?.("(hover: hover) and (pointer: fine)").matches) return;
    const youtubeId = this.getYoutubeId(this.video);
    if (!youtubeId) return;
    if (this.youtubePreviewUrl) return;

    if (this._hoverPreviewTimer) clearTimeout(this._hoverPreviewTimer);
    // Small delay prevents loading iframes during incidental hovers.
    this._hoverPreviewTimer = setTimeout(() => {
      this.youtubePreviewUrl = this.buildYoutubeEmbedUrl(youtubeId);
    }, 250);
  }

  onHoverEnd() {
    if (this._hoverPreviewTimer) clearTimeout(this._hoverPreviewTimer);
    this._hoverPreviewTimer = null;
    // Removing the iframe stops playback immediately.
    this.youtubePreviewUrl = null;
  }

  private getYoutubeId(video: Video | undefined | null): string | null {
    if (!video) return null;
    const platform = (video.platform || "").toLowerCase();
    if (!platform.includes("youtube")) return null;
    return this.extractYoutubeId(video.platform_id);
  }

  private extractYoutubeId(value: string | undefined | null): string | null {
    if (!value) return null;
    const v = value.trim();

    // Raw YouTube video ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(v)) return v;

    // Common URL formats: youtu.be/<id>, youtube.com/watch?v=<id>, /embed/<id>, /shorts/<id>
    const urlMatch = v.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    if (urlMatch?.[1]) return urlMatch[1];

    const vParamMatch = v.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (vParamMatch?.[1]) return vParamMatch[1];

    return null;
  }

  private buildYoutubeEmbedUrl(videoId: string): SafeResourceUrl {
    const id = encodeURIComponent(videoId);
    const url =
      "https://www.youtube.com/embed/" +
      id +
      "?" +
      [
        "autoplay=1",
        // Autoplay is most reliable when muted.
        "mute=1",
        "controls=0",
        "modestbranding=1",
        "rel=0",
        "playsinline=1",
        "iv_load_policy=3",
        "fs=0",
        "disablekb=1",
        "showinfo=0",
        // Loop the same video for a "preview" feel.
        "loop=1",
        "playlist=" + id,
      ].join("&");

    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  copyLink(video, event) {
    this.performingListAction = true;
    this._clipboardService.copy(environment.fenderUrl + "learn/browse?viewingVideo=" + this.video.id);
    event.stopPropagation();
    this.copied = true;
    setTimeout(() => {
      this.copied = false;
    }, 1000);
  }

  chooseLessonOrFolder(type) {
    const modalRef = this.appService.showModal(ChooseLessonComponent, {
      size: "lg",
    });
    modalRef.componentInstance.video = this.video;
    modalRef.componentInstance.type = type;
  }

  findParentLessons() {
    let parents = [];
    this.contentService.lessons.forEach((lesson) => {
      if (lesson.video_ids && lesson.video_ids.includes(this.video.id)) parents.push(lesson);
    });
    return parents;
  }

  toggleApprove(event) {
    this.performingListAction = true;
    if (this.video.status == "pending") {
      this.video.status = "approved";
      this.contentService.saveVideo({ id: this.video.id, status: this.video.status }).subscribe();
    } else {
      let isPartOfApprovedLesson = false;
      let parents = this.findParentLessons();
      if (parents && parents.length) {
        parents.forEach((lesson) => {
          if (lesson.status == "approved") isPartOfApprovedLesson = true;
        });
      }
      if (isPartOfApprovedLesson) {
        alert(this.translate.instant("video.cannot_unapprove_part_of_lesson"));
      } else {
        this.video.status = this.video.status == "approved" ? "pending" : "approved";
        this.contentService.saveVideo({ id: this.video.id, status: this.video.status }).subscribe();
      }
    }
    event.stopPropagation();
  }

  add(event) {
    this.performingListAction = true;
    this.added.next();
    event.stopPropagation();
  }

  remove(event) {
    this.performingListAction = true;
    this.removed.next();
    event.stopPropagation();
  }

  pin(event) {
    event.stopPropagation();
    if (!this.user) {
      this.appService.redirectToConnect(true, false);
      return;
    }
    this.performingListAction = true;
    this.loadingLibraryAction = true;
    this.contentService
      .saveUserVideoData({
        user_id: this.user.id,
        video_id: this.video.id,
        saved: this.contentService.userLearnData.videos[this.video.id] && this.contentService.userLearnData.videos[this.video.id].saved ? false : true,
      })
      .subscribe({
        next: (result) => {
          this.loadingLibraryAction = false;
        },
        error: (error) => {
          this.appService.alertError(error);
        },
      });
  }

  removeFromKeepWatching() {
    if (!this.user) {
      this.appService.redirectToConnect(true, false);
      return;
    }
    this.performingListAction = true;
    this.loadingLibraryAction = true;
    this.contentService
      .saveUserVideoData({
        user_id: this.user.id,
        video_id: this.video.id,
        marked_done: true,
      })
      .subscribe({
        next: (result) => {},
        error: (error) => {
          this.appService.alertError(error);
        },
      });
    event.stopPropagation();
  }

  editVideo() {
    this.performingListAction = true;
    const modalRef = this.appService.showModal(EditVideoComponent, {
      size: "lg",
    });
    modalRef.componentInstance.video = this.video;
    modalRef.result.then(
      (result) => {},
      (error) => {}
    );
  }

  selectTag(tag, e) {
    this.performingListAction = true;
    e.stopPropagation();
    this.router.navigate(["/learn/videos"], { queryParams: { tag: tag.string_id } });
  }

  addTo(event) {
    this.performingListAction = true;
    event.stopPropagation();
    this.translate.get(["video.add_to_lesson_action", "video.add_to_folder_action"]).subscribe((translations: any) => {
      const addToLessonText = translations["video.add_to_lesson_action"];
      const addToFolderText = translations["video.add_to_folder_action"];
      let args: any = {
        title: this.video.title,
        actions: [addToLessonText, addToFolderText],
      };
      this.eventBusService.emit(this.eventBusService.types.showActionChooser, args);
      this.eventBusService
        .observe(this.eventBusService.types.actionChosen)
        .pipe(take(1))
        .subscribe((result) => {
          if (result) {
            this.chooseLessonOrFolder(result == addToLessonText ? "lessons" : "folders");
          }
        });
    });
  }

  /** Accessible label for the focusable video card: optional "Item X of Y: " prefix when in rail, then title and duration */
  get videoItemAriaLabel(): string {
    const title = this.video?.title ?? "";
    const duration = this.video?.duration ? this.helperService.hhmmss(this.video.duration, true) : "";
    const base = duration ? `${title}, ${duration}` : title;
    if (this.itemIndex != null && this.totalCount != null) {
      const prefix = this.translate.instant("video.item_x_of_y", { index: this.itemIndex, total: this.totalCount });
      return `${prefix}${base}`;
    }
    return base;
  }

  /** Focus the focusable card (used by parent rail when Enter to browse). */
  focus(): void {
    this.focusableCard?.nativeElement?.focus();
  }

  /** Tabindex for option buttons: in rail only when this card is entered; otherwise -1 so Tab moves card to card. */
  get optionsTabIndex(): number {
    if (this.tabIndex === -1) return -1;
    if (this.tabIndex === 0) return this.isCardEntered ? 0 : -1;
    return 0;
  }

  /** Focus the first option button (Copy link). Used when user presses Enter on card to enter options. */
  focusFirstOption(): void {
    const el = this._elementRef.nativeElement as HTMLElement;
    const firstOption = el.querySelector(".video-action-btn") || el.querySelector("button");
    if (firstOption) (firstOption as HTMLElement).focus();
  }

  onCardKeydown(event: Event): void {
    if ((event as KeyboardEvent).key === "Enter" && this.tabIndex === 0 && !this.isCardEntered) {
      this.cardEntered.emit();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onCardKeyup(event: Event, video: Video): void {
    if ((event as KeyboardEvent).key === "Enter" && this.tabIndex === 0 && !this.isCardEntered) return;
    this.videoClicked(video, event);
  }

  onFocusOut(event: FocusEvent): void {
    if (!this.isCardEntered || event.relatedTarget == null) return;
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Node)) this.exited.emit();
  }

  videoClicked(video, event) {
    if (!this.performingListAction) {
      if (this.admin || this.showAdd || this.showRemove || this.selectable) {
        this.videoClickedEvent.next(video);
      } else {
        let args: any = {
          id: this.video.platform_id,
          title: this.video.title,
          type: this.video.platform,
          thumb: this.video.image,
          presonus_id: this.video.id,
          ref_caller_element: this.itemContainer,
        };
        if (this.lessonID) args.lesson = this.contentService.lessonMap[this.lessonID];
        this.eventBusService.emit(this.eventBusService.types.playVideo, args);
      }
      if (event) event.stopPropagation();
    }
  }
}
